import { adminRoutePaths } from '@dermloop/static-values';
import type {
  AuthenticationScreenType,
  DermloopEnvironment,
  DermloopRegion,
} from '@dermloop/static-values';

export const ACTIVE_AUTHENTICATION_SCREEN = 'activeAuthenticationScreen';
export const INVITATION_CODE = 'invitationCode';
export const CY_INVITATION_CODE = 'testInvitationCode';
export const INVITATION_EMAIL = 'invitationEmail';
export const CY_INVITATION_EMAIL = 'testInvitationEmail';
// Realm-signed invitation token. Named to match the authorize-request param the
// Keycloak invitation authenticator reads, so the app forwards it unchanged.
export const INVITATION_TOKEN = 'dermloop_invitation';
export const INVITATION_EXPIRY_DATE = 'expiryDate';

// Where to land after a hosted login. returnTo is navigated to after the OIDC callback,
// and a single-use credential must not survive into the URL the user lands on.
const CREDENTIAL_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  INVITATION_TOKEN,
];

export function currentReturnTo(): string {
  const params = new URLSearchParams(window.location.search);
  CREDENTIAL_PARAMS.forEach((param) => params.delete(param));
  const search = params.toString();
  return (
    window.location.pathname +
    (search ? `?${search}` : '') +
    window.location.hash
  );
}
export const CY_INVITATION_EXPIRY_DATE = new Date(
  Date.now() + 30 * 24 * 60 * 60 * 1000,
);

/**
 * Given an environment and region, return the base url for the dermloop api, excluding https:// and port.
 * @param environment
 * @param region
 * @returns
 */
export const dermloopBaseUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
): string => {
  const baseDomain = region === 'us' ? 'dermloop.us' : 'dermloop.io';

  switch (environment) {
    case 'development':
    case 'ci':
      return '127.0.0.1';
    case 'test':
      return `test.${baseDomain}`;
    case 'production':
      return baseDomain;
  }
};

/**
 * Returns melatech base url.
 * @param region
 * @returns
 */
export const melatechBaseUrl = (): string => {
  return 'melatech.io';
};

/**
 * Function for getting Dermloop Web base url based on DermloopEnvironment, including http:// or https://.
 * @param environment DermloopEnvironment string value.
 * @param referralFlow Optional referral flow to base url upon. If not specified will use the eu url.
 * @param uniqueLinkParameter Optional url query parameter
 * @returns url String
 */
export const dermloopWebBaseUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  uniqueLinkParameter?: string,
): string => {
  const baseUrl = dermloopBaseUrl(environment, region);

  switch (environment) {
    case 'development':
    case 'ci':
      return `http://${baseUrl}:4200${uniqueLinkParameter ? `?${uniqueLinkParameter}` : ''}`;
    case 'test':
    case 'production':
      return `https://${baseUrl}${uniqueLinkParameter ? `?${uniqueLinkParameter}` : ''}`;
  }
};

export function dermloopWebPatientResponseUrl(
  environment: DermloopEnvironment,
  region: DermloopRegion,
  patientResponseId: string,
  identifierType: string,
  challengeUuid: string,
): string {
  return `${dermloopWebBaseUrl(environment, region)}/patient-response/${patientResponseId}?patientIdentifierType=${identifierType}&challenge=${challengeUuid}`;
}

/**
 * Function for getting Dermloop Web clinical condition url based on environment is production.
 * @param environment
 * @param region
 * @param healthRecordId
 * @param lesionId
 * @returns
 */
export const dermloopWebClinicConditionUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  healthRecordId: string,
  lesionId: string,
): string => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/clinic/${healthRecordId}/${lesionId}`;
};

/**
 * Function for getting Dermloop Web tele cases url based on environment is production.
 */
export const dermloopWebTeleCasesUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
): string => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/tele`;
};

/**
 * Function for getting Dermloop Web eConsults url based on environment is production.
 */
export const dermloopWebAdminEConsultsUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
): string => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/administration/${adminRoutePaths.eConsults}`;
};

/**
 * Function for getting Dermloop Web unit users url based on environment and region.
 * @param environment
 * @param region
 * @param expiringUsers
 * @returns
 */
export const dermloopWebUnitUsersUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  expiringUsers?: boolean,
) => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/administration/${adminRoutePaths.users}/${expiringUsers ? '?expiringUsers=true' : ''}`;
};

export const dermloopWebConsentUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  consentRevisionId: string,
) => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/consent/rev/${consentRevisionId}`;
};

export const dermloopWebInviteUserUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  invitationCode: string,
  existingUser: boolean,
  invitationEmail: string,
  expiryDate: Date,
) => {
  const defaultAuthenticationScreen: AuthenticationScreenType = !existingUser
    ? 'register'
    : 'none';

  return dermloopWebBaseUrl(
    environment,
    region,
    `${INVITATION_CODE}=${invitationCode}&${ACTIVE_AUTHENTICATION_SCREEN}=${defaultAuthenticationScreen}${invitationEmail ? `&${INVITATION_EMAIL}=${encodeURIComponent(invitationEmail)}` : ''}&${INVITATION_EXPIRY_DATE}=${expiryDate.toISOString()}`,
  );
};

/**
 * Function for getting Dermloop Web tasks url based on environment is production.
 */
export const dermloopWebTasksUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
): string => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/tasks`;
};

/**
 * Base url for the internal Melatech admin app, including protocol.
 */
export const dermloopAdminBaseUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
): string => {
  switch (environment) {
    case 'development':
    case 'ci':
      return 'http://localhost:8100';
    case 'test':
    case 'production':
      return `https://admin.${dermloopBaseUrl(environment, region)}`;
  }
};

/**
 * Returns the url for a support request in the admin app.
 */
export const dermloopAdminSupportRequestUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  supportRequestId: string,
): string => {
  return `${dermloopAdminBaseUrl(environment, region)}/support-requests/${supportRequestId}`;
};

/**
 * Returns the url for the admin medical units page.
 */
export const dermloopAdminMedicalUnitsUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
): string => {
  const baseUrl = dermloopWebBaseUrl(environment, region);

  switch (environment) {
    case 'development':
    case 'ci':
      return `${baseUrl}:8100/user-management/medical-units`;
    case 'test':
    case 'production':
      return `admin.${baseUrl}/user-management/medical-units`;
  }
};

export const dermloopAvatarModelUrl = (
  environment: DermloopEnvironment,
  region: DermloopRegion,
  genderName: string,
): string => {
  const baseUrl = dermloopWebBaseUrl(environment, region);
  return `${baseUrl}/avatar?gender=${encodeURIComponent(genderName)}`;
};
